import { useState } from 'react';
import { EVENT_COLORS, suggestShort, type StudentFlagDef } from '@td/shared';
import {
  useCreateFlag,
  useDeleteFlag,
  useRestoreFlag,
  useStudentFlags,
  useUpdateFlag,
} from '../hooks/useStudentFlags';
import { ApiRequestError } from '../lib/api';
import { useConfirm } from './ConfirmDialog';
import { Modal } from './Modal';

/** Create, rename, recolour, hide and restore student flags. */
export function FlagManager({ onClose }: { onClose: () => void }) {
  const { data: flags = [] } = useStudentFlags();
  const create = useCreateFlag();
  const update = useUpdateFlag();
  const remove = useDeleteFlag();
  const restore = useRestoreFlag();
  const confirm = useConfirm();

  const [label, setLabel] = useState('');
  const [short, setShort] = useState('');
  const [color, setColor] = useState<string>(EVENT_COLORS[0]);
  const [editing, setEditing] = useState<StudentFlagDef | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visible = flags.filter((f) => !f.hidden);
  const hidden = flags.filter((f) => f.hidden);

  async function add() {
    setError(null);
    try {
      await create.mutateAsync({ label, short: short || undefined, color });
      setLabel('');
      setShort('');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That flag could not be added.');
    }
  }

  async function saveEdit() {
    if (!editing) return;
    setError(null);
    try {
      await update.mutateAsync({
        key: editing.key,
        label: editing.label,
        short: editing.short,
        color: editing.color,
      });
      setEditing(null);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That change could not be saved.');
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Flags"
      className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <div className="mb-1 flex items-start justify-between">
        <h2 className="text-lg">Flags</h2>
        <button onClick={onClose} aria-label="Close" className="text-stone hover:text-espresso">
          ✕
        </button>
      </div>
      <p className="mb-4 text-xs text-stone">
        Rename or recolour any of these. Removing one takes it off every student who had it.
      </p>

      <ul className="mb-4 space-y-1.5">
        {visible.map((f) =>
          editing?.key === f.key ? (
            <li key={f.key} className="rounded-card border border-clay bg-linen/40 p-3">
              <div className="mb-2 flex gap-2">
                <input
                  className="field py-1.5"
                  value={editing.label}
                  maxLength={40}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  aria-label="Flag name"
                />
                <input
                  className="field w-20 py-1.5 text-center uppercase"
                  value={editing.short}
                  maxLength={5}
                  onChange={(e) => setEditing({ ...editing, short: e.target.value })}
                  aria-label="Badge text"
                />
              </div>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {EVENT_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setEditing({ ...editing, color: c })}
                    aria-label={`Use ${c}`}
                    className={[
                      'h-6 w-6 rounded-full border-2 transition-transform',
                      editing.color === c ? 'scale-110 border-espresso' : 'border-transparent',
                    ].join(' ')}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditing(null)}
                  className="btn-secondary px-3 py-1 text-xs"
                >
                  Cancel
                </button>
                <button onClick={() => void saveEdit()} className="btn-primary px-3 py-1 text-xs">
                  Save
                </button>
              </div>
            </li>
          ) : (
            <li
              key={f.key}
              className="group flex items-center gap-2 rounded-lg border border-sand px-2.5 py-1.5"
            >
              <span
                className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide"
                style={{ backgroundColor: `${f.color}2E`, color: f.color }}
              >
                {f.short}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-espresso">{f.label}</span>
              {f.builtin && <span className="text-[10px] text-stone">built in</span>}
              <span className="flex shrink-0 gap-2 text-xs opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <button onClick={() => setEditing(f)} className="btn-soft">
                  Edit
                </button>
                <button
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Remove "${f.label}"?`,
                      body: 'It will be taken off every student who has it.',
                      confirmLabel: 'Remove',
                      danger: true,
                    });
                    if (ok) remove.mutate(f.key);
                  }}
                  className="btn-soft-danger"
                >
                  Remove
                </button>
              </span>
            </li>
          ),
        )}
      </ul>

      {hidden.length > 0 && (
        <div className="mb-4">
          <p className="label">Removed</p>
          <ul className="flex flex-wrap gap-1.5">
            {hidden.map((f) => (
              <li key={f.key}>
                <button
                  onClick={() => restore.mutate(f.key)}
                  className="rounded-lg border border-dashed border-sand px-2 py-1 text-xs text-stone hover:border-clay hover:text-espresso"
                >
                  {f.label} · restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-sand pt-3">
        <p className="label">Add a flag</p>
        <div className="mb-2 flex gap-2">
          <input
            className="field py-1.5"
            placeholder="Small group"
            value={label}
            maxLength={40}
            onChange={(e) => setLabel(e.target.value)}
            aria-label="New flag name"
          />
          <input
            className="field w-20 py-1.5 text-center uppercase"
            placeholder={suggestShort(label) || 'SMGR'}
            value={short}
            maxLength={5}
            onChange={(e) => setShort(e.target.value)}
            aria-label="Badge text"
          />
        </div>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {EVENT_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              aria-label={`Use ${c}`}
              className={[
                'h-6 w-6 rounded-full border-2 transition-transform',
                color === c ? 'scale-110 border-espresso' : 'border-transparent',
              ].join(' ')}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <button
          onClick={() => void add()}
          className="btn-primary px-3 py-1.5 text-xs"
          disabled={!label.trim() || create.isPending}
        >
          {create.isPending ? 'Adding…' : 'Add flag'}
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
          {error}
        </p>
      )}
    </Modal>
  );
}
